/**
 * InviteFriendsSection: mint a one-use referral code + share link.
 *
 * Generate-only: no redeem/paste-code field here. Redemption happens only at
 * the signup/conversion transition (see `auth-service.ts`), not from a
 * logged-in account settings screen.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Gift } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useAuthStore } from '@/lib/stores/authStore';
import { SaveAccountCard } from '@/components/account/SaveAccountCard';
import { REFERRAL_GRANT_PM } from '@danda/shared';

export function InviteFriendsSection() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  // Guests can't mint codes server-side (an unproven guest's invite is low-value,
  // see the referral spec). Let them save their account right here — converting
  // flips `isAnonymous`, re-rendering this into the real invite UI below.
  if (user?.isAnonymous) {
    return (
      <div className="mx-auto max-w-xl space-y-8">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">Invite friends</h1>
          <p className="text-sm text-muted-foreground">
            Save your account to invite friends. You both get {REFERRAL_GRANT_PM} PMT when they sign
            up.
          </p>
        </header>
        <SaveAccountCard description="You're playing as a guest. Add an email and password (or use Google) to keep your hero and start inviting friends for PMT." />
      </div>
    );
  }

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const { shareUrl } = await apiClient.generateInviteCode();
      setShareUrl(shareUrl);
    } catch {
      toast.error('Failed to generate an invite code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy, select and copy the link manually');
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Invite friends</h1>
        <p className="text-sm text-muted-foreground">
          Invite a friend to Polymyth. When they sign up, you both get extra {REFERRAL_GRANT_PM} PMT
          to play more.
        </p>
      </header>

      <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
        <Gift className="h-4 w-4 shrink-0 text-amber-500" />
        {shareUrl ? (
          <>
            <span className="flex-1 truncate font-mono text-sm">{shareUrl}</span>
            <Button size="sm" variant="outline" onClick={handleCopy}>
              <Copy className="mr-1 h-4 w-4" />
              Copy
            </Button>
          </>
        ) : (
          <span className="flex-1 text-sm text-muted-foreground">
            No code generated yet this session.
          </span>
        )}
      </div>

      <Button onClick={handleGenerate} disabled={loading}>
        {loading ? 'Generating…' : 'Generate code'}
      </Button>
    </div>
  );
}
